/* eslint-disable react-hooks/set-state-in-effect, @typescript-eslint/no-explicit-any, no-extra-boolean-cast */
import { faTrashXmark } from "@fortawesome/pro-regular-svg-icons";
import { faRotateLeft, faSave } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AlertProps, Box, Button, Collapse, Stack } from "@mui/material";
import { useSnackbar } from "notistack";
import { createContext, useContext, useEffect, useState } from "react";
import { ApiResultAlert } from "./ApiResultAlert";

export function FormProvider(props: {
  children: React.ReactNode;
  fullHeight?: boolean;
  onSave: (form: FormState) => void;
  onCancel: (form: FormState) => void;
  onDelete: (form: FormState) => void;
  showSaveButton?: boolean;
  showCancelButton?: boolean;
  showDeleteButton?: boolean;
  showControls?: boolean;
}) {
  const [isAlertShowing, setIsAlertShowing] = useState<boolean>(false);
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const [isDisabled, setDisabled] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const snackbar = useSnackbar();
  const showSaveButton = props.showSaveButton === undefined || props.showSaveButton === true;
  const showCancelButton = props.showCancelButton === undefined || props.showCancelButton === true;
  const showDeleteButton = props.showDeleteButton === undefined || props.showDeleteButton === true;
  const showControls = props.showControls === undefined || props.showControls === true;

  useEffect(() => {
    if (alert) {
      setIsAlertShowing(true);
      // only auto-dismiss success messages
      if (alert.severity === "success") {
        setTimeout(() => setIsAlertShowing(false), 5000);
      }
    }
  }, [alert]);

  const handleSave = () => {
    props.onSave?.(context);
  };

  const handleDelete = () => {
    props.onDelete?.(context);
  };

  const handleCancel = () => {
    props.onCancel?.(context);
  };

  const notifySuccess = (message?: string) => {
    snackbar.enqueueSnackbar(message, { variant: "success" });
    setAlert({ severity: "success", children: <ApiResultAlert title={message} /> });
  };

  const notifyError = (message?: string, error?: unknown) => {
    console.error(message, error);
    snackbar.enqueueSnackbar(message, { variant: "error" });
    if (error) {
      setAlert({ severity: "error", children: <ApiResultAlert title={message} error={error} /> });
    } else {
      setAlert({ severity: "error", children: message });
    }
  };

  const dismissAlert = () => {
    setIsAlertShowing(false);
  };

  const context = {
    notifySuccess,
    notifyError,
    dismissAlert,
    isDisabled,
    setDisabled,
    isLoading,
    setLoading,
    save: handleSave,
    cancel: handleCancel,
    delete: handleDelete,
  };

  return (
    <FormContext.Provider value={context}>
      <Box component="form" sx={{ display: "flex", flexDirection: "column", flexGrow: props.fullHeight ? 1 : 0 }} noValidate autoComplete="off">
        <Collapse in={isAlertShowing}>
          <Box sx={{ mb: 2 }}>
            <Alert sx={{ "& .MuiAlert-icon": { paddingTop: 1 }, borderRadius: 2 }} {...alert} />
          </Box>
        </Collapse>

        {props.children}

        {showControls && <Box flexGrow={1} />}

        {showControls && (
          <Stack direction="row" gap={2} sx={{ flexWrap: "wrap" }}>
            {showSaveButton && (
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{ height: 50, width: { xs: "100%", md: "inherit" } }}
                startIcon={<FontAwesomeIcon icon={faSave} />}
              >
                Save Changes
              </Button>
            )}
            {showCancelButton && (
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{ height: 50, width: { xs: "100%", md: "inherit" } }}
                startIcon={<FontAwesomeIcon icon={faRotateLeft} />}
              >
                Undo Changes
              </Button>
            )}
            <Box flexGrow={1} sx={{ display: { xs: "none", md: "block" } }} />
            {showDeleteButton && (
              <Button
                variant="outlined"
                color="error"
                onClick={handleDelete}
                sx={{ height: 50, width: { xs: "100%", md: "inherit" } }}
                startIcon={<FontAwesomeIcon icon={faTrashXmark} />}
              >
                Delete
              </Button>
            )}
          </Stack>
        )}
      </Box>
    </FormContext.Provider>
  );
}

export interface FormState {
  notifySuccess: (message?: string) => void;
  notifyError: (message?: string, error?: any) => void;
  dismissAlert: () => void;
  isDisabled: boolean;
  setDisabled: (flag: boolean) => void;
  isLoading: boolean;
  setLoading: (flag: boolean) => void;
  save: () => void;
  delete: () => void;
  cancel: () => void;
}

const initialState: FormState = {
  notifySuccess: (message?: string) => null,
  notifyError: (message?: string, error?: any) => null,
  dismissAlert: () => null,
  isDisabled: false,
  setDisabled: (flag: boolean) => null,
  isLoading: false,
  setLoading: (flag: boolean) => null,
  save: () => null,
  delete: () => null,
  cancel: () => null,
};

export const FormContext = createContext(initialState);

export const useForm = () => {
  return useContext(FormContext);
};
